import { useEffect, useState } from 'react';
import type { LimosaDetail, LimosaPerson } from '@/api/administratie';
import { LimosaUploadForm } from './LimosaUploadForm';

const RECORD_STATUS: Record<LimosaDetail['status'], string> = {
  te_controleren: 'Wordt gecontroleerd',
  goedgekeurd: 'Goedgekeurd',
  afgekeurd: 'Afgekeurd',
};

function fullDate(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

function HistoryList({ records, withName, testId }: { records: LimosaDetail[]; withName?: boolean; testId: string }) {
  return (
    <ul className="mt-2 divide-y divide-gray-100" data-testid={testId}>
      {records.map((r) => (
        <li key={r.id} className="py-2 text-sm">
          {withName && <p className="text-xs font-semibold text-td-muted">{r.partnerName}</p>}
          <div className="flex flex-wrap justify-between gap-2">
            <span className="text-td-navy">{fullDate(r.geldigVan)} – {fullDate(r.geldigTot)}</span>
            <span className="text-xs font-semibold text-td-muted">{RECORD_STATUS[r.status]}</span>
          </div>
          {r.status === 'afgekeurd' && r.opmerking && <p className="text-xs text-red-600">{r.opmerking}</p>}
        </li>
      ))}
    </ul>
  );
}

/**
 * One person's Limosa as a sheet over the planning. In "upload" mode the upload
 * form comes first, "Voor wie" pre-filled; in "details" mode only the history.
 * Older declarations of the login's own team or company (no selectable person)
 * stay readable below, never uploadable.
 */
export function LimosaUploadSheet({
  people,
  records,
  partnerId,
  taskId,
  mode = 'upload',
  onUploaded,
  onClose,
}: {
  /** The job card it is opened from, if any. */
  taskId?: number;
  people: LimosaPerson[];
  records: LimosaDetail[];
  partnerId: number;
  mode?: 'upload' | 'details';
  onUploaded: () => void;
  onClose: () => void;
}) {
  const [selected, setSelected] = useState(partnerId);
  const earlier = records.filter((r) => r.partnerId === selected);
  const peopleIds = new Set(people.map((p) => p.id));
  const other = records.filter((r) => !peopleIds.has(r.partnerId));
  const name = people.find((p) => p.id === selected)?.name;

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={mode === 'upload' ? 'Limosa opladen' : 'Limosa details'}
        className="w-full sm:max-w-md max-h-[90vh] overflow-y-auto bg-gray-50 rounded-t-2xl sm:rounded-2xl p-4 pb-[calc(1rem+env(safe-area-inset-bottom,0px))] space-y-3"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-extrabold text-td-navy">Limosa</h2>
            {mode === 'details' && name && <p className="text-sm text-td-muted">{name}</p>}
          </div>
          <button type="button" onClick={onClose} className="min-w-[44px] min-h-[44px] text-sm font-semibold text-td-muted">
            Sluiten
          </button>
        </div>

        {mode === 'upload' && (
          <LimosaUploadForm
            people={people}
            initialPartnerId={partnerId}
            taskId={taskId}
            onPartnerChange={setSelected}
            onUploaded={() => {
              onUploaded();
              onClose();
            }}
          />
        )}

        <section className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="font-semibold text-td-navy">Eerdere uploads</h3>
          {earlier.length === 0
            ? <p className="mt-1 text-sm text-td-muted">Nog geen uploads.</p>
            : <HistoryList records={earlier} testId="limosa-history" />}
        </section>

        {other.length > 0 && (
          <section className="bg-white rounded-xl border border-gray-200 p-4">
            <h3 className="font-semibold text-td-navy">Overige eerdere uploads</h3>
            <p className="mt-1 text-xs text-td-muted">Oudere aangiftes van het team of bedrijf. Alleen ter inzage.</p>
            <HistoryList records={other} withName testId="limosa-history-other" />
          </section>
        )}
      </div>
    </div>
  );
}
